import {type SquareGateway, type ProcessedTokenResult} from '../../models/square';
import {store} from '../../store';
import {getLocaleText} from '../../util/translation';
import {handleTokenResult} from './square';
import {$qsAll} from '../../../../@shared/ts/dom';
import {type AfterpayClearpay, PaymentMethodAlreadyDestroyedError, PaymentMethodUnsupportedError, PaymentMethodNotAttachedError, type Payments} from '@square/web-payments-sdk-types';
import afterpayImageURL from '../../../img/marks/afterpay.svg';
import {PeachPayCustomer} from '../../reducers/peachPayCustomerReducer';
import {Feature} from '../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../@type/features';
import {GatewayEligibility, type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {captureSentryException} from '../../../../@shared/ts/sentry';
import {render} from '../../reducers/rootReducer';
import {type PaymentRequest} from '@square/web-payments-sdk-types';
import {debounce} from '../../util/debounce';
import {PaymentConfiguration} from '../../reducers/paymentConfigurationReducer';

function initSquareAfterpayMethod(): SquareGateway {
	let afterpay: AfterpayClearpay | null = null;
	let buttonInstanceWaitingInterval: NodeJS.Timer | undefined;

	const config: GatewayConfiguration = {
		name: getLocaleTitle(),
		gatewayId: 'peachpay_square_afterpay',
		description: getDescription(),
		assets: {
			title: {src: afterpayImageURL},
			badge: {src: afterpayImageURL},
		},
		hasCustomButton: true,
	};

	store.subscribe(() => {
		config.name = getLocaleTitle();
		$qsAll('.pp-pm-type[data-gateway="peachpay_square_afterpay"]', $element => {
			const $title = $element.querySelector('span');
			if ($title) {
				$title.innerText = config.name;
			}
		});
		// Don't worry about re-rendering pp-pm-more options, it is already re-rendered
		// on billing country update event.
	});

	const debouncedActivate = debounce((async (payments: Payments, paymentRequest: PaymentRequest) => {
		if (afterpay) {
			return;
		}

		if (PaymentConfiguration.eligibleGateway(config.gatewayId) !== GatewayEligibility.Eligible) {
			config.initialized = false;
			store.dispatch(render());
			return;
		}

		try {
			afterpay = await payments.afterpayClearpay(paymentRequest);

			config.initialized = true;
			store.dispatch(render());
		} catch (error: unknown) {
			config.initialized = false;
			store.dispatch(render());

			console.error('Square Afterpay/Clearpay activation error:');
			console.error(error);
			captureSentryException(error as Error);
		}
	}) satisfies SquareGateway['activate']);

	return {
		config,
		activate: debouncedActivate,
		reset() {
			clearInterval(buttonInstanceWaitingInterval);

			const oldInstance = afterpay;
			afterpay = null;

			oldInstance?.destroy()?.catch(error => {
				if (error instanceof PaymentMethodAlreadyDestroyedError) {
					return;
				}

				console.error(error);
				captureSentryException(error as Error);
			});
		},
		attach(selector: string) {
			clearInterval(buttonInstanceWaitingInterval);
			buttonInstanceWaitingInterval = setInterval(() => {
				if (afterpay) {
					clearInterval(buttonInstanceWaitingInterval);

					afterpay.attach(selector, {
						buttonColor: 'black',
						buttonType: 'checkout_with_afterpay',
						finalCtaButtonType: 'buy_now',
					})?.catch(error => {
						config.initialized = false;
						store.dispatch(render());

						if (error instanceof PaymentMethodUnsupportedError) {
							console.log(error);
							return;
						}

						console.error(error);
						captureSentryException(error as Error, {selector});
					});
				}
			}, 200);
		},
		detach() {
			clearInterval(buttonInstanceWaitingInterval);

			afterpay?.detach()?.catch(error => {
				if (error instanceof PaymentMethodNotAttachedError) {
					return;
				}

				config.initialized = false;
				store.dispatch(render());

				if (error instanceof PaymentMethodAlreadyDestroyedError) {
					afterpay = null;
					return;
				}

				console.error(error);
				captureSentryException(error as Error);
			});
		},

		async tokenize(): Promise<ProcessedTokenResult> {
			try {
				if (!afterpay) {
					return [null, null, {
						paymentStatus: 'creationfailed',
						note: 'Failure processing Afterpay/Clearpay payment.',
					}];
				}

				const result = await afterpay.tokenize();
				return handleTokenResult(result);
			} catch (error: unknown) {
				const update: ITransactionUpdate = {
					paymentStatus: 'creationfailed',
					note: 'An unknown exception occured while tokenizing the payment.',
				};

				if (error instanceof Error) {
					update.note = error.message;
				}

				return [null, null, update];
			}
		},
	};
}

function getLocaleTitle() {
	const title = Feature.metadata<string>(FeatureFlag.SQUARE_AFTERPAY, 'title');
	if (title && !(title === 'Afterpay' || title === 'Clearpay')) {
		return title;
	}

	const country = PeachPayCustomer.billing.country();

	switch (country) {
		case 'GB':
		case 'ES':
		case 'FR':
		case 'IT': {
			return 'Clearpay';
		}

		default: {
			return 'Afterpay';
		}
	}
}

function getDescription(): string {
	const title = getLocaleTitle();
	return /* html */ `
<img style="display: block; text-align: left; height: 1.5rem; " src=${afterpayImageURL}>
<p style="text-align: left; margin: 0.5rem 0;">${title} ${getLocaleText('selected for checkout.')}</p>
<hr/>
<p style="text-align: left; margin: 0.5rem 0 0;" class="muted">${getLocaleText('Another step will appear after submitting your order to complete your purchase details.')}<p>`;
}

export {initSquareAfterpayMethod};
